import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { SubscriptionOptions } from '@reduxjs/toolkit/query';

import {
    selectFacilityId,
    selectSageSupportGroupId,
    selectCareGroupId,
    selectNurseMedtechGroupId,
} from 'src/store/configSlice';
import {
    StaffMember,
    useGetStaffMembersQuery,
    StaffMemberGroupMembership,
} from 'src/store/sageAdminApi';

function useFacilityStaff(excludeSageSupport = true, queryOptions: SubscriptionOptions = {}) {
    const facilityId = useSelector(selectFacilityId);
    const sageSupportGroupId = useSelector(selectSageSupportGroupId);
    const careGroupId = useSelector(selectCareGroupId);
    const nurseMedtechGroupId = useSelector(selectNurseMedtechGroupId);

    const {
        pollingInterval = 0,
        refetchOnFocus = false,
        refetchOnReconnect = false,
    } = queryOptions;

    const {
        data: allStaffMembers,
        isLoading: isLoadingStaffMembers,
        isFetching: isFetchingStaffMembers,
        refetch: refetchStaffMembers,
    } = useGetStaffMembersQuery(
        { facilityId },
        { pollingInterval, refetchOnFocus, refetchOnReconnect, skip: !facilityId }
    );

    const {
        ridToStaffMap,
        sageSupportMembersMap,
        careStaffMembersMap,
        onCallStaffRidsSet,
        onCallNurseMedtechRidsSet,
        onCallStaffByCareZoneRid,
    } = useMemo(
        () =>
            (allStaffMembers ?? []).reduce(
                (acc, staffMember) => {
                    const staffMemberId = staffMember.id!;

                    const {
                        groupMemberships = [],
                        onCallStates = [],
                        staffMemberFacilityConfigs = [],
                    } = staffMember;

                    acc.ridToStaffMap.set(staffMemberId, staffMember);

                    // Part 1: Handle group memberships
                    const careStaffGroupMembership = groupMemberships.find(
                        (groupMembership) => groupMembership.groupId === careGroupId
                    );
                    if (careStaffGroupMembership) {
                        acc.careStaffMembersMap.set(staffMemberId, careStaffGroupMembership);
                    }

                    const sageSupportGroupMembership = groupMemberships.find(
                        (groupMembership) => groupMembership.groupId === sageSupportGroupId
                    );
                    if (sageSupportGroupMembership) {
                        acc.sageSupportMembersMap.set(staffMemberId, sageSupportGroupMembership);

                        if (excludeSageSupport) {
                            // Do not include Sage Support members in the on call counts
                            return acc;
                        }
                    }

                    // Part 2: Handle on call states by care zone
                    const facilityOnCallState = onCallStates.find(
                        (onCallState) => onCallState.facilityId === facilityId
                    );
                    if (facilityOnCallState?.isOnCall) {
                        acc.onCallStaffRidsSet.add(staffMemberId);

                        const staffMemberCareZonesInFacility =
                            staffMemberFacilityConfigs.find(
                                (config) => config.facilityId === facilityId
                            )?.careZones || [];

                        // Add to relevant care zone arrays
                        for (const careZone of staffMemberCareZonesInFacility) {
                            const careZoneId = careZone?.id;
                            if (!careZoneId) {
                                continue;
                            }

                            if (!acc.onCallStaffByCareZoneRid[careZoneId]) {
                                // eslint-disable-next-line no-param-reassign
                                acc.onCallStaffByCareZoneRid[careZoneId] = [];
                            }
                            acc.onCallStaffByCareZoneRid[careZoneId].push(staffMember);
                        }
                    }

                    // Part 3: Handle on call nurse/medtech rids

                    const nurseMedtechGroupMembership = groupMemberships.find(
                        (groupMembership) => groupMembership.groupId === nurseMedtechGroupId
                    );

                    if (nurseMedtechGroupMembership && facilityOnCallState?.isOnCall) {
                        acc.onCallNurseMedtechRidsSet.add(staffMemberId);
                    }

                    return acc;
                },
                {
                    ridToStaffMap: new Map<string, StaffMember>(),
                    sageSupportMembersMap: new Map<string, StaffMemberGroupMembership>(),
                    careStaffMembersMap: new Map<string, StaffMemberGroupMembership>(),
                    onCallStaffRidsSet: new Set<string>(),
                    onCallNurseMedtechRidsSet: new Set<string>(),
                    onCallStaffByCareZoneRid: {} as { [careZoneId: string]: StaffMember[] },
                } as {
                    ridToStaffMap: Map<string, StaffMember>;
                    sageSupportMembersMap: Map<string, StaffMemberGroupMembership>;
                    careStaffMembersMap: Map<string, StaffMemberGroupMembership>;
                    onCallStaffRidsSet: Set<string>;
                    onCallNurseMedtechRidsSet: Set<string>;
                    onCallStaffByCareZoneRid: { [careZoneId: string]: StaffMember[] };
                }
            ),
        [
            allStaffMembers,
            careGroupId,
            excludeSageSupport,
            facilityId,
            nurseMedtechGroupId,
            sageSupportGroupId,
        ]
    );

    const staffMembers = useMemo(() => {
        const filteredStaffMembers =
            (excludeSageSupport
                ? allStaffMembers?.filter(
                      (staffMember) => !sageSupportMembersMap.has(staffMember.id!)
                  )
                : allStaffMembers) || [];

        return filteredStaffMembers.map((staffMember) => {
            const { staffMemberFacilityConfigs = [] } = staffMember;

            // update each staff member to filter out null careZones from their staffMemberFacilityConfigs
            const updatedStaffMemberFacilityConfigs = staffMemberFacilityConfigs.map((config) => ({
                ...config,
                careZones: config.careZones?.filter((careZone) => !!careZone),
            }));

            return {
                ...staffMember,
                staffMemberFacilityConfigs: updatedStaffMemberFacilityConfigs,
            };
        });
    }, [excludeSageSupport, allStaffMembers, sageSupportMembersMap]);

    return {
        isLoading: isLoadingStaffMembers,
        isFetching: isFetchingStaffMembers,
        refetchStaffMembers,
        staffMembers,
        staffMembersMap: ridToStaffMap,
        onCallStaffByCareZoneRid,
        careGroupMembersMap: careStaffMembersMap,
        onCallStaffRids: onCallStaffRidsSet,
        onCallNurseMedtechs: onCallNurseMedtechRidsSet,
    };
}

export default useFacilityStaff;
